import { useCallback, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import {
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
  type TransportRequestError,
} from "@/transport"

import type { ArchiveAgentMutationRequest } from "@/types_api/backend/transport/archive_agent_mutation_request"
import type { CreateAgentMutationRequest } from "@/types_api/backend/transport/create_agent_mutation_request"
import type { AgentMutationResource } from "@/types_api/backend/transport/list_agent_mutations_response"
import type { UpdateAgentMutationRequest } from "@/types_api/backend/transport/update_agent_mutation_request"
import { asJsonObjectValueOrNull, asStringRecordOrNull } from "@/types/json"

import type { AgentMutation } from "./types"

export type AgentMutationsListData = AgentMutation[] | null
export type AgentMutationsAllData = AgentMutation[] | null

interface UseAgentMutationsListOptions<T> {
  enabled?: boolean
  select?: (data: AgentMutationsListData) => T
}

interface UseAgentMutationsAllOptions<T> {
  enabled?: boolean
  select?: (data: AgentMutationsAllData) => T
}

interface UseAgentMutationTransportActionsOptions {
  agentId: string
  onCreateSuccess?: () => void
  onCreateError?: (error: TransportRequestError) => void
  onUpdateSuccess?: () => void
  onUpdateError?: (error: TransportRequestError) => void
  onArchiveSuccess?: () => void
  onArchiveError?: (
    error: TransportRequestError,
    variables: ArchiveAgentMutationRequest,
  ) => void
}

function normalizeAgentMutationResource(
  mutation: AgentMutationResource,
): AgentMutation {
  return {
    id: mutation.id,
    agent_id: mutation.agent_id,
    display_name: mutation.display_name,
    description: mutation.description ?? "",
    config_overrides: asJsonObjectValueOrNull(mutation.config_overrides) ?? {},
    parameter_values: asStringRecordOrNull(mutation.parameter_values) ?? {},
    status: mutation.status ?? "ACTIVE",
    create_time: mutation.create_time,
    update_time: mutation.update_time ?? mutation.create_time,
  }
}

function normalizeAgentMutations(
  mutations: AgentMutationResource[],
): AgentMutation[] {
  return mutations.map(normalizeAgentMutationResource)
}

export function useAgentMutationsTransportList<T = AgentMutationsListData>(
  agentId: string,
  options?: UseAgentMutationsListOptions<T>,
) {
  const { enabled, select } = options ?? {}
  const request = useMemo(() => ({ agent_id: agentId }), [agentId])

  return useTransportQuery({
    operation: "listAgentMutations",
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled: enabled !== undefined ? enabled && !!agentId : !!agentId,
      select: (response) => {
        const data = normalizeAgentMutations(response.mutations)
        return select ? select(data) : (data as T)
      },
    },
  })
}

export function useAgentMutationsTransportAll<T = AgentMutationsAllData>(
  options?: UseAgentMutationsAllOptions<T>,
) {
  const { enabled, select } = options ?? {}

  return useTransportQuery({
    operation: "listAllAgentMutations",
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      select: (response) => {
        const data = normalizeAgentMutations(response.mutations)
        return select ? select(data) : (data as T)
      },
    },
  })
}

export function useAgentMutationTransportActions({
  agentId,
  onArchiveError,
  onArchiveSuccess,
  onCreateError,
  onCreateSuccess,
  onUpdateError,
  onUpdateSuccess,
}: UseAgentMutationTransportActionsOptions) {
  const transportQueryUtils = useTransportQueryUtils()

  const invalidateMutations = useCallback(() => {
    void transportQueryUtils.invalidate("listAgentMutations", {
      agent_id: agentId,
    })
    void transportQueryUtils.invalidate("listAllAgentMutations")
  }, [agentId, transportQueryUtils])

  const createMutation = useTransportMutation("createAgentMutation", {
    onSuccess: () => {
      invalidateMutations()
      onCreateSuccess?.()
    },
    onError: onCreateError,
  })
  const updateMutation = useTransportMutation("updateAgentMutation", {
    onSuccess: () => {
      invalidateMutations()
      onUpdateSuccess?.()
    },
    onError: onUpdateError,
  })
  const archiveMutation = useTransportMutation("archiveAgentMutation", {
    onSuccess: () => {
      invalidateMutations()
      onArchiveSuccess?.()
    },
    onError: onArchiveError,
  })

  const create = useCallback(
    async (data: Omit<CreateAgentMutationRequest, "agent_id">) => {
      const response = await createMutation.mutateAsync({
        agent_id: agentId,
        ...data,
      })
      return normalizeAgentMutationResource(response.mutation)
    },
    [agentId, createMutation],
  )

  const update = useCallback(
    async (
      mutationId: string,
      data: Omit<UpdateAgentMutationRequest, "agent_id" | "mutation_id">,
    ) => {
      const response = await updateMutation.mutateAsync({
        agent_id: agentId,
        mutation_id: mutationId,
        ...data,
      })
      return normalizeAgentMutationResource(response.mutation)
    },
    [agentId, updateMutation],
  )

  const archive = useCallback(
    async (mutationId: string) => {
      await archiveMutation.mutateAsync({
        agent_id: agentId,
        mutation_id: mutationId,
      })
    },
    [agentId, archiveMutation],
  )

  const forceArchive = useCallback(
    async (mutationId: string) => {
      await archiveMutation.mutateAsync({
        agent_id: agentId,
        mutation_id: mutationId,
        force: true,
      })
    },
    [agentId, archiveMutation],
  )

  return {
    archive,
    create,
    forceArchive,
    isArchiving: archiveMutation.isPending,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    update,
  }
}
